import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../app/hooks';
import { setToken } from '../features/auth/authSlice';
import { DecodedToken } from '../types/api';

const welcomedKey = (userId: number) => `welcomed_${userId}`;

// Shared by Login.tsx (password + social) and Signup.tsx (social, via an
// invite link): decodes the token, and either reports the account is still
// pending admin approval, or logs the user in and navigates -- to the club
// directory if they aren't in any club at all, to the one-time animated
// welcome screen on a first-ever successful login, or straight to Activities
// on every login after that.
export function useCompleteLogin() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    return (token: string): { pending: boolean } => {
        const decodedToken: DecodedToken = JSON.parse(atob(token.split('.')[1]));

        // A valid account with no club membership at all (e.g. they left
        // every club they'd joined) -- not an error, just route them to
        // pick a club instead of the pending-approval message below, which
        // only makes sense once they've actually requested to join one.
        if (decodedToken.clubId === null) {
            dispatch(setToken(token));
            navigate('/clubs');
            return { pending: false };
        }

        if (decodedToken.role === null) {
            return { pending: true };
        }

        dispatch(setToken(token));

        const key = welcomedKey(decodedToken.id);
        if (!localStorage.getItem(key)) {
            localStorage.setItem(key, '1');
            navigate('/welcome');
        } else {
            navigate('/activities', { state: { justLoggedIn: true } });
        }

        return { pending: false };
    };
}
